import type {
	DocumentGenerationVersion,
	DocumentTypes,
	DocumentUniqueId,
} from "./types";
import {
	GENERATION_VERSION_SEPARATOR,
	HISTORY_VERSION_SEPARATOR,
	TYPE_SEPARATOR,
	documentTypedIdStringSchema,
	idDocumentTypeStringMap,
} from "./validation";
import type { DocumentHistoryVersion, DocumentTypeString } from "./validation";

export function parseDocumentIdString(idString: string): {
	type: DocumentTypes;
	id: DocumentUniqueId;
	generationVersion?: DocumentGenerationVersion;
	historyVersion?: DocumentHistoryVersion;
} {
	const parsedId = documentTypedIdStringSchema.parse(idString);

	const dashIndex = parsedId.indexOf(TYPE_SEPARATOR);
	const documentTypeString = parsedId.substring(
		0,
		dashIndex,
	) as DocumentTypeString;
	const remaining = parsedId.substring(dashIndex + 1);

	const generationVersionIndex = remaining.indexOf(
		GENERATION_VERSION_SEPARATOR,
	);
	const historyVersionIndex = remaining.indexOf(HISTORY_VERSION_SEPARATOR);
	const hasGenerationVersion = generationVersionIndex !== -1;
	const hasHistoryVersion = historyVersionIndex !== -1;

	const generationVersion = hasGenerationVersion
		? (Number.parseInt(
				remaining.substring(
					generationVersionIndex + 1,
					hasHistoryVersion ? historyVersionIndex : undefined,
				),
				10,
			) as DocumentGenerationVersion)
		: undefined;

	const historyVersion = hasHistoryVersion
		? (Number.parseInt(
				remaining.substring(historyVersionIndex + 1),
				10,
			) as DocumentHistoryVersion)
		: undefined;

	const uniqueIdEndIndex = Math.min(
		hasGenerationVersion ? generationVersionIndex : remaining.length,
		hasHistoryVersion ? historyVersionIndex : remaining.length,
	);
	const documentUniqueId = remaining.substring(
		0,
		uniqueIdEndIndex,
	) as DocumentUniqueId;

	const typeEntry = Object.entries(idDocumentTypeStringMap).find(
		([, value]) => value === documentTypeString,
	);

	if (!typeEntry) {
		throw new Error(`Unknown document type string: ${documentTypeString}`);
	}

	const documentType = typeEntry[0] as DocumentTypes;

	return {
		type: documentType,
		id: documentUniqueId,
		generationVersion,
		historyVersion,
	};
}
